import { FieldArray, FieldArrayRenderProps } from 'react-final-form-arrays';
import { connect } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';
import { ARRAY_ERROR } from 'final-form';
import { FormSpy } from 'react-final-form';
import { useCallback } from 'react';

import { ShowMoreButton } from 'UIPackage/MainModule/components/ShowMoreButton/ShowMoreButton';
import { getPlaceBlockId, PLACES_BLOCK_ID } from 'KnmErknmPackage/MainModule/constants/knmErknmFieldsElementsIds';
import { KnmAddressField } from 'KnmPackage/MainModule/components/KnmAddressField';
import { getKnmFormRef, getKnmSystemType } from 'KnmPackage/MainModule/selectors/knmSelectors';
import { IKnmFormRef } from 'KnmPackage/MainModule/interfaces/IKnmFormRef';
import { IKnmErknmFormPlace } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmFormPlace';
import { Button } from 'UIPackage/MainModule/components/Button';
import { CloseButton } from 'UIPackage/MainModule/components/CloseButton';
import { ErknmFillInProcessMessage } from 'KnmErknmPackage/MainModule/components/ErknmFillInProcessMessage';
import { FieldBlock } from 'UIPackage/FormModule/components/FieldBlock';
import { Row } from 'UIPackage/FormModule/components/Row';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { KnmSystemTypesEnum } from 'KnmPackage/MainModule/enums/KnmSystemTypesEnum';

import styles from './ErknmPlaces.module.scss';
import { ErknmFieldMark } from '../../MainModule/components/ErknmFieldMark';
import { ErknmApproveRemarkAddButton } from '../../ErknmApproveRemarkModule/components/ErknmApproveRemarkAddButton';
import { ErknmApproveRemarkSectionCodeEnum } from '../../ErknmApproveRemarkModule/enums/ErknmApproveRemarkSectionCodeEnum';
import { ErknmApproveRemark } from '../../ErknmApproveRemarkModule/components/ErknmApproveRemark';
import { useIsPlacesEnabled } from '../../MainModule/fieldsAvailability/erknmPlacesFieldsAvailability';
import { ErknmApproveRemarkChanges } from '../../ErknmApproveRemarkModule/components/ErknmApproveRemarkChanges';
import { useShowMore } from '../../../hooks/useShowMore';
import { Spinner } from '../../../UIPackage/MainModule/components/Spinner';

interface IErknmPlacesProps {
  isWatchOnly: boolean;
}

interface IErknmPlacesStoreProps {
  formRef: IKnmFormRef | undefined;
  knmSystemType?: KnmSystemTypesEnum;
}

const ErknmPlacesWithoutStore = ({
  formRef,
  knmSystemType,
  isWatchOnly,
}: IErknmPlacesStoreProps & IErknmPlacesProps) => {
  const push = formRef?.current.mutators.push;
  const remove = formRef?.current.mutators.remove;
  const isEnabled = useIsPlacesEnabled() && !isWatchOnly;

  const addField = useCallback(() => {
    if (push && isEnabled) {
      const addingField: IKnmErknmFormPlace = {
        value: undefined,
        guid: undefined,
        dateCreate: undefined,
        listId: uuidv4(),
      };

      push('places', addingField);
    }
  }, [isEnabled, push]);

  const makeRemoveField = (index: number) => () => isEnabled && remove && remove('places', index);

  return (
    <FieldBlock
      label={
        <>
          Место (места) проведения контрольного (надзорного) мероприятия
          <ErknmFieldMark fieldName="places" />
        </>
      }
      id={PLACES_BLOCK_ID}
      button={
        <>
          <Button
            className="shared-btn-add"
            text="Добавить"
            type="button"
            variant="link"
            color="primary"
            id="erknmPlacesAddButton"
            disabled={!isEnabled}
            onClick={addField}
          />
          <ErknmApproveRemarkAddButton sectionCode={ErknmApproveRemarkSectionCodeEnum.PLACES} />
        </>
      }
      fillInProcess={<ErknmFillInProcessMessage name="places" />}
    >
      <FormSpy subscription={{ errors: true, touched: true }}>
        {props => {
          const fieldError = props.errors?.places?.[ARRAY_ERROR];
          const isFieldTouched = props.touched?.places;

          if (isFieldTouched && fieldError) {
            return <p className="shared-input-error shared-row__small-margin">{fieldError}</p>;
          }

          return null;
        }}
      </FormSpy>
      <ErknmApproveRemark sectionCode={ErknmApproveRemarkSectionCodeEnum.PLACES} />
      <FieldArray name="places">
        {({ fields }: FieldArrayRenderProps<IKnmErknmFormPlace, HTMLElement>) => {
          const { visibleItems, hasMore, showMore, totalCount, visibleCount, isRendering } = useShowMore(fields.value, {
            initialCount: 10,
            incrementCount: 10,
          });

          if (!fields.length) {
            return null;
          }

          return (
            <>
              <Row>
                {visibleItems.map((fieldValue: IKnmErknmFormPlace) => {
                  const index =
                    fields.value?.findIndex((item: IKnmErknmFormPlace) => item.listId === fieldValue.listId) ?? 0;
                  const fieldName = `places[${index}]`;
                  const id = getPlaceBlockId(fieldValue.listId);

                  return (
                    <div className={styles.PlacesRow} key={id} id={id}>
                      <KnmAddressField
                        type="deleting-row"
                        id={`${fieldName}.value`}
                        addressFieldPathWithPrefix={`${fieldName}.value`}
                        clearable
                        placeholder={
                          <>
                            <ErknmFieldMark fieldName={`${fieldName}.value`} />
                          </>
                        }
                        resize="none"
                        disabled={!isEnabled}
                        required
                        formRef={formRef}
                        prefixNameNotInFias={`${fieldName}.`}
                        prefixNameMatchesObjectAddress={`${fieldName}.`}
                        knmSystemType={knmSystemType}
                        manualAddressPlaceholder={
                          <>
                            Место
                            <ErknmFieldMark fieldName={`${fieldName}.manualAddress`} />
                          </>
                        }
                        regionPlaceholder={
                          <>
                            Субъект РФ
                            <ErknmFieldMark fieldName={`${fieldName}.region`} />
                          </>
                        }
                        isRegionTextField={!!fieldValue.regionName}
                      />
                      {(!fieldValue.guid || isEnabled) && (
                        <CloseButton className="shared-deleting-row-btn" onClick={makeRemoveField(index)} />
                      )}
                      <ErknmApproveRemarkChanges
                        sectionCode={ErknmApproveRemarkSectionCodeEnum.PLACES}
                        guid={fieldValue.guid}
                      />
                    </div>
                  );
                })}
              </Row>
              {isRendering && <Spinner className={styles.Loader} />}
              {hasMore && !isRendering && (
                <ShowMoreButton onClick={showMore} totalCount={totalCount} visibleCount={visibleCount} />
              )}
            </>
          );
        }}
      </FieldArray>
    </FieldBlock>
  );
};

const mapStateToProps = (state: IRootState): IErknmPlacesStoreProps => ({
  formRef: getKnmFormRef(state),
  knmSystemType: getKnmSystemType(state),
});

export const ErknmPlaces = connect(mapStateToProps)(ErknmPlacesWithoutStore);
